import React from 'react';
import { get } from 'lodash-es';
import moment from 'moment';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { of, concat, merge, BehaviorSubject, from, EMPTY } from 'rxjs';
import { filter, catchError, mergeMap, tap, takeUntil, switchMap, finalize } from 'rxjs/operators';

import {
  NotificationService,
  NOTIFICATION_TYPE_INFO,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

import Global from '../../store/global-selectors';
import { selectLabelQueBatches } from '../label-que/label-que.selectors';
import * as Actions from './csw.actions';
import * as Api from './csw.api';
import { runNowAndRetryAfterCostReview } from './csw.cost-review.observables';
import { startBulkRatingGetMessages } from './bulk-rate.observables';
import { bulkRateFulfillmentPlans } from '../bulk/bulk.api';
import { handleLabelPurchaseSuccess, handleLabelPurchaseFailure } from './csw.label-purchase.helpers';
import { HelpArticleLink } from '../../components/help-article-link';
import { emptyObject } from 'ui-components/default-props/empty';
import { createLabelSubject } from '../../fulfillment-planner/context';
import { LabelBatchStatusStep } from '../../enums/label-batch-status-step';
import { UserSettings } from '../../enums/user-settings/user-settings';
import { UserSettingsOptions } from '../../enums/user-settings/user-settings-options';
import { getUserFriendlyMessagesForErrorResponse } from '../../errors/user-friendly-error-message-service';
import { PrepaidDuty, Recipient } from '../../fulfillment-planner/duties-and-taxes/constants';
import { getIsServiceLocalDelivery } from '../../hooks/use-is-service-local-delivery';
import { findSellerProviderIdNeedingPickupPrompt } from '../../services/carrier-pickup-helper';
import { convertDateToMomentDateObjAtMidnightInSellerTimeZone } from '../../services/datetime-service';
import { linkIDs } from '../../services/link-service/links';
import { labelBatchListener } from '../../services/pusher-service/persistent-listeners';
import * as BulkActions from '../../store/bulk/bulk.actions';
import * as InventoryActions from '../../store/inventory/inventory.actions';
import { createLabelsForBatches } from '../batches/grid/batches.grid.api';
import { makeBulkCreateLabelObservable, labelPurchasePusherRace } from '../bulk/bulk.label.observables';
import { runLabelPurchaseGate$ } from '../../label-purchase-gate/label-purchase-gate';
import { BULK_PURCHASE_COST_REVIEW_CANCEL } from '../bulk/purchase/bulk.purchase.actions';
import { startBulkPurchaseCostReview, waitForEndOfCostReview } from '../bulk/purchase/bulk.purchase.cost-review.observables';
import { shouldShowSingleLabelCostReview } from '../cost-review/cost-review.helpers';
import {
  hasShowErrorsEnabled,
  determineShouldShowErrorsOrWarnings,
  getQuickshipPropsFromState,
  UPS_INVOICED_NOTIFICATION_ERROR_CODE,
} from '../quickship/quickship.helpers';
import { bulkLoadFulfillmentPlans$ } from '../bulkload/bulkload.observables';

const messages = defineMessages({
  creatingLabelTitle: {
    description: 'orderDetailsEpics.creatingLabelTitle',
    defaultMessage: 'Processing...',
    id: 'BmyaSk',
  },
  creatingLabelContent: {
    description: 'orderDetailsEpics.creatingLabelContent',
    defaultMessage: 'Purchasing label from carrier.',
    id: 'alYqLI',
  },
  creatingLocalDeliveryContent: {
    defaultMessage: 'Requesting pickup from carrier.',
    id: 'Kcx12n',
    description: 'Local Delivery Notification Content',
  },
  sendingExternalFulfillmentTitle: {
    description: 'orderDetailsEpics.sendingExternalFulfillmentTitle',
    defaultMessage: 'Sending request.',
    id: 'GxbPTC',
  },
  sendingExternalFulfillmentContent: {
    description: 'orderDetailsEpics.sendingExternalFulfillmentContent',
    defaultMessage: 'Sending your external fulfillment request.',
    id: 'lktR8+',
  },
  popupErrorsTitle: {
    description: 'error message when label cannot be created title',
    defaultMessage: 'ShipStation was unable to create the label',
    id: 'j14VML',
  },
  popupErrorsMessage: {
    description: 'error message when label cannot be created message',
    defaultMessage:
      'Check the label alert for details about the problem. You can also review our <a>error message help article</a> for steps that may resolve the issue. If the issue persists, contact our support team for further assistance.',
    id: 'KcVuDq',
  },
});

const shouldRunLabelBeforeCostReview = ({ shouldUseQuickship, userQuickshipSettings = [] }) => {
  // this is just an optimization that will save us a cost review API call if it's not necessary
  const userQuickshipShowCostSummarySetting =
    userQuickshipSettings.find((setting) => setting.settingName === UserSettings.quickshipShowCostSummary.settingName) || {};
  return (
    !shouldUseQuickship ||
    userQuickshipShowCostSummarySetting?.settingValue === UserSettingsOptions.quickshipNeverShowCostSummary.settingValue
  );
};

/**
 * @param {object} args
 * @param {() => object} args.getState - Redux getState function
 * @param {string?} [args.serviceId] - Optional. If passed in it will be used to see if the service allows local delivery and change the message content. If not passed in it is assumed the service does not support local delivery
 * @returns {string}
 */
export const showCreatingLabelNotification = ({ getState, serviceId }) => {
  const isLocalDelivery = serviceId ? getIsServiceLocalDelivery(getState(), serviceId) : false;
  const content = isLocalDelivery ? (
    <FormattedMessage {...messages.creatingLocalDeliveryContent} />
  ) : (
    <FormattedMessage {...messages.creatingLabelContent} />
  );
  const creatingLabelNotificationProps = {
    title: <FormattedMessage {...messages.creatingLabelTitle} />,
    content,
    notificationType: NOTIFICATION_TYPE_INFO,
    dontHide: false,
  };
  return NotificationService.showNotification({
    ...creatingLabelNotificationProps,
  });
};

export const showErrorCreatingLabelNotification = (dontHide = true) => {
  const creatingLabelErrorNotificationProps = {
    title: <FormattedMessage {...messages.popupErrorsTitle} />,
    content: (
      <FormattedMessage
        {...messages.popupErrorsMessage}
        values={{
          a: (chunks) => <HelpArticleLink articleId={linkIDs.CommonLabelRateErrors}>{chunks}</HelpArticleLink>,
        }}
      />
    ),
    notificationType: NOTIFICATION_TYPE_ERROR,
    dontHide,
  };
  return NotificationService.showNotification({
    ...creatingLabelErrorNotificationProps,
  });
};

const singleLabelStopListeningToCostReview = (action$) => {
  return action$.pipe(ofType(Actions.CSW_CREATE_LABEL_SUCCESS, BULK_PURCHASE_COST_REVIEW_CANCEL));
};

const error$ = (
  responseData = {},
  {
    notificationId,
    isPersonalShipsuranceAccount,
    action$,
    getState,
    fulfillmentPlanId,
    doNotPrint,
    autoPrint,
    defaultPrinterAutoPrintSetting,
    shipDate,
    labelObject,
    quickshipProps,
    hasAlreadyViewedCostReview = false,
    serviceId,
    shouldBypassCostReview = false,
  },
) => {
  NotificationService.removeNotification(notificationId);
  const { errors, stringErrors } = getUserFriendlyMessagesForErrorResponse(responseData);

  // mapping errors to match that of the handleLabelPurchaseFailure errorResponse
  // Pick & Pack Station bypasses the cost-review flow entirely — its purchase failures surface
  // inline (legacy Scan page parity) instead of opening the cost review modal.
  const shouldShowCostReview =
    !shouldBypassCostReview &&
    shouldShowSingleLabelCostReview({
      responseData,
      quickshipProps,
      hasAlreadyViewedCostReview,
    });
  const isUpsInvoicedNotification = errors.some((err) => err.apiError?.Code === UPS_INVOICED_NOTIFICATION_ERROR_CODE);

  if (!isPersonalShipsuranceAccount && shouldShowCostReview) {
    return merge(
      startBulkPurchaseCostReview({
        action$,
        included: [fulfillmentPlanId],
        andPrint: !doNotPrint,
        shouldShowLegacyCostReview: true,
        getState,
        launchUpsInvoicedModalOnTop: isUpsInvoicedNotification,
      }),
      waitForEndOfCostReview(action$).pipe(
        takeUntil(singleLabelStopListeningToCostReview(action$)),
        mergeMap(({ jobGuid }) => {
          const labelNotificationId = showCreatingLabelNotification({
            getState,
            serviceId,
          });
          return concat(
            of(
              BulkActions.bulkSetActiveLabelBatchStatusStep({
                step: LabelBatchStatusStep.Loading.ordinal,
              }),
            ),
            of(BulkActions.clearCostReview(jobGuid)),
            makeBulkCreateLabelObservable({
              action$,
              shipDate,
              fulfillmentPlanIds: [fulfillmentPlanId],
              logContext: {
                purchasePoint: 'csw',
                labelObject,
              },
              getState,
            }).pipe(
              filter((next) => !next?.apiSuccess),
              tap(() => NotificationService.removeNotification(labelNotificationId)),
              mergeMap(({ finalMessage, friendlyErrors, errorResponse }) => {
                if (finalMessage) {
                  return handleLabelPurchaseSuccess({
                    fulfillmentPlanId,
                    labelObject,
                    autoPrint,
                    doNotPrint,
                    defaultPrinterAutoPrintSetting,
                    getState,
                    action$,
                  });
                }
                return handleLabelPurchaseFailure({
                  fulfillmentPlanId,
                  errorResponse,
                  friendlyErrors,
                  doNotPrint,
                  getState,
                  action$,
                });
              }),
            ),
          );
        }),
      ),
    );
  }

  const labelErrors = errors.length ? errors.map((err) => ({ ...err.messageDescriptor, values: err.values })) : stringErrors;

  if (determineShouldShowErrorsOrWarnings(quickshipProps) && !shouldBypassCostReview) {
    showErrorCreatingLabelNotification();
  }
  return concat(
    of(Actions.createLabelFailure(fulfillmentPlanId, labelErrors, undefined, true)),
    of(InventoryActions.deallocateFulfillmentPlansSilently([fulfillmentPlanId])), // Hacky: FP is allocated on label creation attempt, if there are label creation errors, attempt to deallocate the FP.
  );
};

const createLabelForExistingBatch = ({
  action$,
  getState,
  batchId,
  fulfillmentPlanId,
  shipDate,
  autoPrint,
  doNotPrint,
  labelObject,
  defaultPrinterAutoPrintSetting,
  quickshipProps,
  errorProps,
  hasAlreadyViewedCostReview = false,
}) => {
  let hasActuallyAlreadyViewedCostReview = hasAlreadyViewedCostReview;
  let notificationId = '';
  const sellerTimeZone = Global.selectSellerTimeZoneIANA(getState());
  const shipDateInUtc = moment.utc(shipDate);
  // This step is needed until we can standardize Ship Dates
  // as outlined in SPD-11186 - Greg S 9/14/22
  const correctedShipDate = convertDateToMomentDateObjAtMidnightInSellerTimeZone(shipDateInUtc, sellerTimeZone).toISOString();
  // if QS + user always wants to see cost review, show them the cost review. Otherwise, do normal path.
  const getCreateLabelsForBulkPipeline = () => {
    notificationId = showCreatingLabelNotification({
      getState,
      serviceId: errorProps.serviceId,
    });
    const errorPropsWithNotificationId = {
      ...errorProps,
      notificationId,
    };
    return createLabelsForBatches(batchId, correctedShipDate).pipe(
      tap(() => NotificationService.removeNotification(notificationId)),
      mergeMap((response) => {
        const parsedBatchId = parseInt(batchId, 10);
        const metadata = {
          fulfillmentPlanId,
          autoPrint,
          doNotPrint,
          labelObject,
          notificationId,
          fromSingleLabel: true,
        };
        const { onStart, onFinish } = labelBatchListener.createSingleJobScopedHandlers();
        const labelBatchIds = response?.labelBatchIds || [batchId];
        onStart(labelBatchIds);

        const sellerProviderIdForPickupPrompt = findSellerProviderIdNeedingPickupPrompt(getState(), [fulfillmentPlanId]);

        return merge(
          of(BulkActions.bulkPurchaseLabelSuccess([fulfillmentPlanId], undefined, undefined, [parsedBatchId], !doNotPrint, metadata)),
          labelPurchasePusherRace({
            action$,
            labelBatchIds: [parsedBatchId],
            onFinish,
            sellerProviderIdForPickupPrompt,
          }).pipe(
            mergeMap(({ finalMessage, friendlyErrors, errorResponse }) => {
              if (finalMessage) {
                const printSettings = Global.selectPrintSettingsLabelPrintSettings(getState());
                return handleLabelPurchaseSuccess({
                  fulfillmentPlanId,
                  labelObject,
                  autoPrint,
                  doNotPrint,
                  defaultPrinterAutoPrintSetting,
                  quickshipProps,
                  printSettings,
                  getState,
                  action$,
                });
              }

              return handleLabelPurchaseFailure({
                fulfillmentPlanId,
                errorResponse,
                friendlyErrors,
                doNotPrint,
                quickshipProps,
                getState,
                action$,
                hasAlreadyViewedCostReview,
                shouldBypassCostReview: errorProps.shouldBypassCostReview,
              });
            }),
          ),
        );
      }),
      catchError((errorResponse) => {
        const errors = get(errorResponse, 'response.data', emptyObject);
        return error$(errors, errorPropsWithNotificationId);
      }),
    );
  };

  const tryRunningNowBeforeCostReview = shouldRunLabelBeforeCostReview(quickshipProps);
  return tryRunningNowBeforeCostReview || hasActuallyAlreadyViewedCostReview
    ? runNowAndRetryAfterCostReview({
        action$,
      }).pipe(mergeMap(getCreateLabelsForBulkPipeline))
    : merge(
        startBulkPurchaseCostReview({
          action$,
          included: [fulfillmentPlanId],
          andPrint: !doNotPrint,
          shouldShowLegacyCostReview: true,
          getState,
          quickshipProps,
          shouldAlwaysShowCostReviewDefault: false,
        }),
        waitForEndOfCostReview(action$).pipe(
          mergeMap(() => {
            hasActuallyAlreadyViewedCostReview = true;
            return getCreateLabelsForBulkPipeline();
          }),
        ),
      );
};

const createLabel = ({
  action$,
  getState,
  shipDate,
  fulfillmentPlanId,
  labelObject,
  errorProps,
  autoPrint,
  doNotPrint,
  defaultPrinterAutoPrintSetting,
  quickshipProps,
  isPersonalShipsuranceAccount,
  shouldBypassCostReview = false,
}) => {
  let hasAlreadyViewedCostReview = false;
  let notificationId = '';
  const getCreateLabelObservable = () => {
    // the value of createLabel$ doesn't matter we just need to trigger the pipe immediately
    const createLabel$ = new BehaviorSubject(null);

    return createLabel$.pipe(
      switchMap(() => {
        notificationId = showCreatingLabelNotification({
          getState,
          serviceId: errorProps.serviceId,
        });
        const errorPropsWithNotificationId = {
          ...errorProps,
          notificationId,
        };

        // Gate runs once here (outer) before the purchase attempt.
        return runLabelPurchaseGate$({
          state: getState(),
          fulfillmentPlanIds: [fulfillmentPlanId],
          nextActionCreator: () => ({ proceed: true }),
        }).pipe(
          switchMap((result) => {
            if (!result?.proceed) {
              NotificationService.removeNotification(notificationId);
              createLabelSubject.next({ fulfillmentPlanId, isCreatingLabel: false });
              // A declined purchase gate never reaches the API — surface an explicit abort so
              // dependent UI (e.g. Pick & Pack Station's isPurchasing lock) can reset.
              return of(Actions.createLabelAborted(fulfillmentPlanId));
            }
            return makeBulkCreateLabelObservable({
              action$,
              shipDate,
              fulfillmentPlanIds: [fulfillmentPlanId],
              logContext: {
                purchasePoint: 'csw',
                labelObject,
              },
              quickshipProps,
              // Pick & Pack Station: cost-review-class errors throw and surface inline instead
              // of opening the cost review modal (legacy Scan page parity).
              shouldThrowApiError: shouldBypassCostReview,
              getState,
            }).pipe(
              filter((next) => !next?.apiSuccess),
              tap(() => NotificationService.removeNotification(notificationId)),
              mergeMap(({ finalMessage, friendlyErrors, hasCostReviewError }) => {
                if (finalMessage) {
                  const printSettings = Global.selectPrintSettingsLabelPrintSettings(getState());
                  return handleLabelPurchaseSuccess({
                    fulfillmentPlanId,
                    labelObject,
                    autoPrint,
                    doNotPrint,
                    defaultPrinterAutoPrintSetting,
                    quickshipProps,
                    printSettings,
                    getState,
                    action$,
                  });
                }
                const shouldShowCostReview = hasCostReviewError && !hasAlreadyViewedCostReview && !shouldBypassCostReview;
                if (determineShouldShowErrorsOrWarnings(quickshipProps) && !shouldShowCostReview && !shouldBypassCostReview) {
                  showErrorCreatingLabelNotification();
                }

                if (shouldShowCostReview) {
                  return merge(
                    of(Actions.createLabelFailure(fulfillmentPlanId, friendlyErrors, !doNotPrint, false)),
                    startBulkPurchaseCostReview({
                      action$,
                      included: [fulfillmentPlanId],
                      excluded: undefined,
                      errored: undefined,
                      andPrint: !doNotPrint,
                      getState,
                      shouldShowLegacyCostReview: true,
                    }),
                    waitForEndOfCostReview(action$).pipe(
                      takeUntil(action$.pipe(ofType(Actions.CSW_CREATE_LABEL_SUCCESS))),
                      mergeMap(({ jobGuid }) => {
                        hasAlreadyViewedCostReview = true;
                        const labelQue = selectLabelQueBatches(getState());
                        const existingLabelBatch = labelQue.find((batch) => batch.fulfillmentPlanId === fulfillmentPlanId);
                        if (!existingLabelBatch) {
                          // if we take this branch then /createlabel2 failed with an insufficient funds error and
                          // no label batch has been created, so we restart from /createlabel2
                          return concat(
                            of(
                              BulkActions.bulkSetActiveLabelBatchStatusStep({
                                step: LabelBatchStatusStep.Loading.ordinal,
                              }),
                            ),
                            of(BulkActions.clearCostReview(jobGuid)),
                          ).pipe(
                            finalize(() => {
                              createLabel$.next(null);
                            }),
                          );
                        }
                        return concat(
                          of(
                            BulkActions.bulkSetActiveLabelBatchStatusStep({
                              step: LabelBatchStatusStep.Loading.ordinal,
                            }),
                          ),
                          of(BulkActions.clearCostReview(jobGuid)),
                          createLabelForExistingBatch({
                            action$,
                            getState,
                            batchId: existingLabelBatch.batchId,
                            fulfillmentPlanId,
                            shipDate,
                            autoPrint,
                            doNotPrint,
                            labelObject,
                            defaultPrinterAutoPrintSetting,
                            quickshipProps,
                            errorProps: {
                              notificationId,
                              isPersonalShipsuranceAccount,
                              action$,
                              getState,
                              fulfillmentPlanId,
                              doNotPrint,
                              autoPrint,
                              defaultPrinterAutoPrintSetting,
                              shipDate,
                              labelObject,
                              quickshipProps,
                              hasAlreadyViewedCostReview,
                              shouldBypassCostReview,
                            },
                            hasAlreadyViewedCostReview,
                          }),
                        );
                      }),
                    ),
                  );
                }
                return of(Actions.createLabelFailure(fulfillmentPlanId, friendlyErrors, !doNotPrint, true));
              }),
              catchError((errorResponse) => {
                const errors = get(errorResponse, 'response.data', emptyObject);
                return error$(errors, errorPropsWithNotificationId);
              }),
            );
          }),
        );
      }),
    );
  };

  const tryRunningNowBeforeCostReview = shouldRunLabelBeforeCostReview(quickshipProps);
  // Pick & Pack Station never enters the cost-review flow — its failures surface inline (legacy
  // Scan page parity), so it always runs the purchase now and throws on cost-review-class errors.
  const shouldRunLabelNow = tryRunningNowBeforeCostReview || shouldBypassCostReview;
  return merge(
    shouldRunLabelNow
      ? getCreateLabelObservable()
      : merge(
          startBulkPurchaseCostReview({
            action$,
            included: [fulfillmentPlanId],
            andPrint: !doNotPrint,
            shouldShowLegacyCostReview: true,
            getState,
            quickshipProps,
            shouldAlwaysShowCostReviewDefault: false,
          }),
          waitForEndOfCostReview(action$).pipe(
            takeUntil(action$.pipe(ofType(Actions.CSW_CREATE_LABEL_SUCCESS))),
            mergeMap(({ jobGuid }) => {
              hasAlreadyViewedCostReview = true;
              const labelQue = selectLabelQueBatches(getState());
              const existingLabelBatch = labelQue.find((batch) => batch.fulfillmentPlanId === fulfillmentPlanId);
              return concat(
                of(
                  BulkActions.bulkSetActiveLabelBatchStatusStep({
                    step: LabelBatchStatusStep.Loading.ordinal,
                  }),
                ),
                of(BulkActions.clearCostReview(jobGuid)),
                !tryRunningNowBeforeCostReview && !existingLabelBatch
                  ? getCreateLabelObservable()
                  : createLabelForExistingBatch({
                      action$,
                      getState,
                      batchId: existingLabelBatch.batchId,
                      fulfillmentPlanId,
                      shipDate,
                      autoPrint,
                      doNotPrint,
                      labelObject,
                      defaultPrinterAutoPrintSetting,
                      quickshipProps,
                      errorProps: {
                        notificationId,
                        isPersonalShipsuranceAccount,
                        action$,
                        getState,
                        fulfillmentPlanId,
                        doNotPrint,
                        autoPrint,
                        defaultPrinterAutoPrintSetting,
                        shipDate,
                        labelObject,
                        quickshipProps,
                        hasAlreadyViewedCostReview,
                        shouldBypassCostReview,
                      },
                      hasAlreadyViewedCostReview,
                    }),
              );
            }),
          ),
        ),
  );
};

// note: this path is inherently quickship-compatible. Use create-label-content for non-quickship pathways.
export const handleCreateLabelRequest = (createLabelProps, action$, getState) => {
  const {
    fulfillmentPlanId,
    autoPrint,
    defaultPrinterAutoPrintSetting,
    doNotPrint,
    labelObject,
    shipDate,
    isPersonalShipsuranceAccount,
    labelQue = [],
    saveConfigAndRate,
    shouldBypassCostReview = false,
  } = createLabelProps;
  const state = getState();
  const quickshipProps = getQuickshipPropsFromState(state);
  const { showErrorsOrWarnings } = Global.selectUserQuickshipShouldShowErrorsOrWarnings(state);
  quickshipProps.showErrorsOrWarnings = showErrorsOrWarnings;
  const { shouldUseQuickship, userQuickshipSettings } = quickshipProps;
  const { serviceId } = saveConfigAndRate;

  const errorProps = {
    notificationId: '',
    isPersonalShipsuranceAccount,
    action$,
    getState,
    fulfillmentPlanId,
    doNotPrint,
    autoPrint,
    defaultPrinterAutoPrintSetting,
    shipDate,
    labelObject,
    quickshipProps,
    serviceId,
    shouldBypassCostReview,
  };

  const shouldAlsoRate = !!saveConfigAndRate?.serviceCollectionGuid;

  const proceedToCreateLabel = () => {
    // let's check to see if that order exists in the label queue already.
    // this can happen if you previously attempted to create a label and got an error.
    const existingLabelBatch = labelQue.find((batch) => batch.fulfillmentPlanId === fulfillmentPlanId);
    if (existingLabelBatch && existingLabelBatch.errors > 0) {
      const { batchId } = existingLabelBatch;
      return createLabelForExistingBatch({
        action$,
        getState,
        batchId,
        fulfillmentPlanId,
        shipDate,
        autoPrint,
        doNotPrint,
        labelObject,
        defaultPrinterAutoPrintSetting,
        quickshipProps,
        errorProps,
      });
    }

    const moreCreateLabelProps = {
      ...createLabelProps,
      autoPrint,
      getState,
      errorProps,
      action$,
      shipDate,
      fulfillmentPlanId,
      labelObject,
      doNotPrint,
      defaultPrinterAutoPrintSetting,
      quickshipProps,
    };
    return createLabel({
      action$,
      ...moreCreateLabelProps,
    });
  };

  return Api.updateFulfillmentPlanConfig(fulfillmentPlanId, saveConfigAndRate, {
    shouldLock: true,
  }).pipe(
    mergeMap((res) => {
      const isShowErrorsEnabled = hasShowErrorsEnabled(userQuickshipSettings);
      if (!shouldUseQuickship || (shouldUseQuickship && isShowErrorsEnabled)) {
        const { Errors } = res;
        if (Errors) {
          return error$(res, errorProps);
        }
      }

      if (!shouldAlsoRate) {
        return proceedToCreateLabel();
      }

      return startBulkRatingGetMessages({
        apiCallback: () => from(bulkRateFulfillmentPlans({ fulfillmentPlanIds: [fulfillmentPlanId], forceRefreshRate: true })),
        pusherTimeoutMs: 180000,
        onError: () => EMPTY,
        logContext: { fulfillmentPlanId, operation: 'create-label-rating' },
      }).pipe(
        mergeMap(({ findMessage$ }) =>
          findMessage$.pipe(
            // Reload fulfillment plan to get updated labelConfiguration (e.g., sellerLabelProviderId for rate shoppers)
            mergeMap(() =>
              bulkLoadFulfillmentPlans$([fulfillmentPlanId]).pipe(
                mergeMap(() => proceedToCreateLabel()),
                catchError(() => proceedToCreateLabel()),
              ),
            ),
            catchError(() => proceedToCreateLabel()),
          ),
        ),
        catchError(() => proceedToCreateLabel()),
      );
    }),
    catchError((errorResponse) => {
      const errors = get(errorResponse, 'response.data', emptyObject);
      return error$(errors, errorProps);
    }),
  );
};

export const showSendingExternalFulfillment = () => {
  const externalInfo = {
    title: <FormattedMessage {...messages.sendingExternalFulfillmentTitle} />,
    content: <FormattedMessage {...messages.sendingExternalFulfillmentContent} />,
    notificationType: NOTIFICATION_TYPE_INFO,
    duration: NOTIFICATION_DURATIONS.MEDIUM,
  };

  return NotificationService.showNotification(externalInfo);
};

export const getConfirmation = (payloadOptions) => {
  if (payloadOptions.containsAlcohol) {
    return '3';
  }
  return null;
};

export const getPrepaidDuty = ({ otherShippingOptions }) => {
  const additionalOptions = otherShippingOptions?.additionalOptions || [];
  const findPrepaidDutyOption = additionalOptions.find(({ additionalOptionEnum }) => additionalOptionEnum === PrepaidDuty);
  return findPrepaidDutyOption?.additionalOptionValue.prepaidDuty || Recipient;
};

export const getCswReturnsFulfillmentPlanId = (salesOrderId) => {
  return `return-${salesOrderId}`;
};
